/**
 * Query params that must never be carried forward into a redirect target. They are
 * transient, per-attempt state of the very flows that build redirects (login bounce, checkout
 * signature validation). Re-embedding them is what lets a redirect URL nest into itself and
 * fall in a redirect loop. See {@link sanitizeRedirectTarget}
 */
export const TRANSIENT_REDIRECT_PARAMS = ['redirect', 'validate', 'token', 'from_unauthorized_code'] as const

/**
 * Normalizes an arbitrary URL into a safe redirect target or null when it must not be used as one
 *
 * Guarantees the result is:
 * - same-origin and relative (pathname + search): blocks open redirects to other hosts
 * - free of {@link TRANSIENT_REDIRECT_PARAMS}: blocks redirect accumulation / loops
 * - never the login page itself: blocks the login↔login bounce
 */
export function sanitizeRedirectTarget(
  rawUrl: string | null | undefined,
  origin: string = window.location.origin,
): string | null {
  if (!rawUrl) return null

  try {
    const url = new URL(rawUrl, origin)

    if (url.origin !== origin) return null
    if (url.pathname === '/login') return null

    for (const param of TRANSIENT_REDIRECT_PARAMS) {
      url.searchParams.delete(param)
    }

    return url.pathname + url.search
  } catch {
    return null
  }
}

/**
 * Whether a redirect target is already safe to use as-is, without going through {@link sanitizeRedirectTarget}
 */
export function isRedirectTargetAlreadySafe(rawUrl: string, origin: string = window.location.origin): boolean {
  try {
    const url = new URL(rawUrl, origin)

    if (url.origin !== origin) return false
    if (url.pathname === '/login') return false

    return !TRANSIENT_REDIRECT_PARAMS.some((param) => url.searchParams.has(param))
  } catch {
    return false
  }
}
